/**
 * Chat presence: the one fact the notification fan-out cannot work out for itself --
 * is this person looking at this conversation right now?
 *
 * Supabase Realtime Presence lives in the Realtime service, not in Postgres, so the
 * notify_chat_message() trigger cannot read it. Instead each signed-in client keeps a
 * row in public.chat_presence up to date, and the trigger reads that.
 *
 * Everything here is pure so the state machine can be tested without a DOM or a clock;
 * usePresenceHeartbeat owns the timers, listeners and the write.
 */

/** How often a foregrounded client refreshes its row. */
export const PRESENCE_HEARTBEAT_MS = 25_000;

/**
 * How long the database trusts a row for. Mirrored as PRESENCE_TTL in
 * supabase/migrations/20260910120000_chat_presence.sql -- change both together.
 *
 * Comfortably above the heartbeat so one dropped beat (a throttled tab, a slow network)
 * does not read as "gone". The gap is also the worst-case delay before a client that
 * crashed or lost signal starts receiving pushes again.
 */
export const PRESENCE_TTL_MS = 60_000;

/**
 * Reading without touching anything for this long stops counting as being here.
 *
 * Without it, a chat left open on a second monitor silences that conversation's pushes
 * forever -- the tab is genuinely visible, so nothing else would ever mark the person
 * away. WhatsApp Web behaves the same way.
 */
export const PRESENCE_IDLE_AFTER_MS = 5 * 60_000;

const DEVICE_ID_KEY = 'volyx-device-id';

export type PresenceInput = {
  /** Document visible on web, app foregrounded on native. */
  visible: boolean;
  /** chatKey() of the thread on screen, or null when no chat is open. */
  activeChatKey: string | null;
  /** When the user last actually did something. */
  lastActivityAt: number;
  now: number;
};

/** The two columns a heartbeat writes. updated_at is always now(). */
export type PresenceRow = {
  app_active: boolean;
  active_chat_key: string | null;
};

export function presenceRow(input: PresenceInput): PresenceRow {
  const active =
    input.visible && input.now - input.lastActivityAt < PRESENCE_IDLE_AFTER_MS;

  // A backgrounded or idle client is in no conversation, whatever was last on screen.
  // Saying otherwise would suppress that thread's pushes while nobody is watching.
  return {
    app_active: active,
    active_chat_key: active ? input.activeChatKey : null,
  };
}

export function presenceRowsEqual(a: PresenceRow | null, b: PresenceRow): boolean {
  return a !== null && a.app_active === b.app_active && a.active_chat_key === b.active_chat_key;
}

/**
 * Write on a change, and otherwise only to keep an active row from going stale.
 *
 * The consequence worth naming: once the row says inactive, nothing rewrites it. It ages
 * out on its own, which is exactly what should happen to a client that has gone away.
 */
export function shouldWritePresence(
  previous: PresenceRow | null,
  next: PresenceRow,
  lastWriteAt: number,
  now: number,
  heartbeatMs: number = PRESENCE_HEARTBEAT_MS
): boolean {
  if (!presenceRowsEqual(previous, next)) return true;
  if (!next.app_active) return false;
  return now - lastWriteAt >= heartbeatMs;
}

let memoryDeviceId: string | null = null;

/**
 * Stable per browser / per install, so a phone in a pocket cannot clear the
 * active_chat_key a desktop just wrote. Falls back to a per-session id where storage is
 * unavailable (private windows, blocked site data) -- a device that forgets its id just
 * leaves one extra row behind to age out.
 */
export function getDeviceId(): string {
  if (memoryDeviceId) return memoryDeviceId;

  try {
    const stored = localStorage.getItem(DEVICE_ID_KEY);
    if (stored) {
      memoryDeviceId = stored;
      return stored;
    }
  } catch {
    // storage unavailable; fall through to a session-only id
  }

  const generated =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  memoryDeviceId = generated;

  try {
    localStorage.setItem(DEVICE_ID_KEY, generated);
  } catch {
    // storage unavailable; the id lives for this session only
  }

  return generated;
}

/** Test seam: forget the memoised id so a test can exercise generation again. */
export function resetDeviceIdForTests(): void {
  memoryDeviceId = null;
}
